import React, { useEffect, useImperativeHandle } from 'react';
import { MarkerProps, ControlPosition } from 'react-map-gl/dist/es5';
import MapboxGeocoder, { GeocoderOptions } from '@mapbox/mapbox-gl-geocoder';
import useControl from './useControl';

// import "@mapbox/mapbox-gl-geocoder/dist/mapbox-gl-geocoder.css"

type GeocoderControlProps = Omit<
    GeocoderOptions,
    'accessToken' | 'mapboxgl' | 'marker'
> & {
    mapboxAccessToken: string;
    marker?: boolean | Omit<MarkerProps, 'longitude' | 'latitude'>;

    position: ControlPosition;

    onLoading?: (e: object) => void;
    onResults?: (e: object) => void;
    onResult?: (e: object) => void;
    onError?: (e: object) => void;
    onMount?: (e: object) => void;
};
const coordinatesGeocoder = function (query) {
    // Match anything which looks like
    // decimal degrees coordinate pair.
    const matches = query.match(
        /^[ ]*(?:Lat: )?(-?\d+\.?\d*)[, ]+(?:Lng: )?(-?\d+\.?\d*)[ ]*$/i,
    );
    if (!matches) {
        return null;
    }

    function coordinateFeature(lng, lat) {
        return {
            center: [lng, lat],
            geometry: {
                type: 'Point',
                coordinates: [lng, lat],
            },
            place_name: 'Lat: ' + lat + ' Lng: ' + lng,
            place_type: ['coordinate'],
            properties: {},
            type: 'Feature',
        };
    }

    const coord1 = Number(matches[1]);
    const coord2 = Number(matches[2]);
    const geocodes = [];

    if (coord1 < -90 || coord1 > 90) {
        // must be lng, lat
        geocodes.push(coordinateFeature(coord1, coord2));
    }

    if (coord2 < -90 || coord2 > 90) {
        // must be lat, lng
        geocodes.push(coordinateFeature(coord2, coord1));
    }

    if (geocodes.length === 0) {
        // else could be either lng, lat or lat, lng
        geocodes.push(coordinateFeature(coord1, coord2));
        geocodes.push(coordinateFeature(coord2, coord1));
    }

    return geocodes;
};

function getPointFromResult(e: any) {
    let data = {
        location: null,
    };

    for (let i = 0; i < e.result.context.length; i++) {
        if (e.result.context[i].id.includes('postcode')) {
            data['postcode'] = e.result.context[i].text;
        }
        if (e.result.context[i].id.includes('locality')) {
            data['line_2'] = e.result.context[i].text;
        }
        if (e.result.context[i].id.includes('place')) {
            data['city'] = e.result.context[i].text;
        }
        if (e.result.context[i].id.includes('district')) {
            data['district'] = e.result.context[i].text;
        }
        if (e.result.context[i].id.includes('country')) {
            data['country'] = e.result.context[i].text;
        }
    }
    // First line of address
    if (e.result.text || e.result.address) {
        data['line_1'] =
            (e.result.address ? e.result.address + ' ' : '') +
            (e.result.text ? e.result.text : '');
    }
    // Full Address
    if (e.result.place_name) {
        data['full_address'] = e.result.place_name;
    }

    const location =
        e.result &&
        (e.result.center ||
            (e.result.geometry?.type === 'Point' &&
                e.result.geometry.coordinates));

    // Coordinates
    if (location) {
        const latLng = {};

        latLng['longitude'] = location[0];
        latLng['latitude'] = location[1];

        data.location = latLng;
    }

    return data;
}
/* eslint-disable complexity,max-statements */
export default React.forwardRef(function GeocoderControl(
    {
        onLoading = noop,
        onResult = noop,
        mapboxAccessToken,
        onError = noop,
        onResults = noop,
        containerNode,
        onClear = noop,
        onMount = noop,
        ...props
    }: GeocoderControlProps,
    ref,
) {
    const geocoder = useControl<MapboxGeocoder>(
        () => {
            const ctrl = new MapboxGeocoder({
                ...props,
                marker: false,
                reverseGeocode: true,
                localGeocoder: coordinatesGeocoder,
                accessToken: mapboxAccessToken,
            });
            ctrl.on('loading', onLoading);
            ctrl.on('results', onResults);
            ctrl.on('clear', onClear);
            ctrl.on('result', (evt) => {
                if (typeof onResult === 'function') {
                    const data = getPointFromResult(evt);
                    onResult(data);
                }
            });
            ctrl.on('error', onError);
            return ctrl;
        },
        () => {},
        {},
        containerNode,
    );

    // @ts-ignore (TS2339) private member
    if (geocoder._map) {
        if (
            geocoder.getProximity() !== props.proximity &&
            props.proximity !== undefined
        ) {
            geocoder.setProximity(props.proximity);
        }
        if (
            geocoder.getRenderFunction() !== props.render &&
            props.render !== undefined
        ) {
            geocoder.setRenderFunction(props.render);
        }
        if (
            geocoder.getLanguage() !== props.language &&
            props.language !== undefined
        ) {
            geocoder.setLanguage(props.language);
        }
        if (geocoder.getZoom() !== props.zoom && props.zoom !== undefined) {
            geocoder.setZoom(props.zoom);
        }
        if (geocoder.getFlyTo() !== props.flyTo && props.flyTo !== undefined) {
            geocoder.setFlyTo(props.flyTo);
        }
        if (
            geocoder.getPlaceholder() !== props.placeholder &&
            props.placeholder !== undefined
        ) {
            geocoder.setPlaceholder(props.placeholder);
        }
        if (
            geocoder.getCountries() !== props.countries &&
            props.countries !== undefined
        ) {
            geocoder.setCountries(props.countries);
        }
        if (geocoder.getTypes() !== props.types && props.types !== undefined) {
            geocoder.setTypes(props.types);
        }
        if (
            geocoder.getMinLength() !== props.minLength &&
            props.minLength !== undefined
        ) {
            geocoder.setMinLength(props.minLength);
        }
        if (geocoder.getLimit() !== props.limit && props.limit !== undefined) {
            geocoder.setLimit(props.limit);
        }
        if (
            geocoder.getFilter() !== props.filter &&
            props.filter !== undefined
        ) {
            geocoder.setFilter(props.filter);
        }
        if (
            geocoder.getOrigin() !== props.origin &&
            props.origin !== undefined
        ) {
            geocoder.setOrigin(props.origin);
        }
        // Types missing from @types/mapbox__mapbox-gl-geocoder
        // if (geocoder.getAutocomplete() !== props.autocomplete && props.autocomplete !== undefined) {
        //   geocoder.setAutocomplete(props.autocomplete);
        // }
        // if (geocoder.getFuzzyMatch() !== props.fuzzyMatch && props.fuzzyMatch !== undefined) {
        //   geocoder.setFuzzyMatch(props.fuzzyMatch);
        // }
        // if (geocoder.getRouting() !== props.routing && props.routing !== undefined) {
        //   geocoder.setRouting(props.routing);
        // }
        // if (geocoder.getWorldview() !== props.worldview && props.worldview !== undefined) {
        //   geocoder.setWorldview(props.worldview);
        // }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useImperativeHandle(ref, () => geocoder, []);

    useEffect(() => {
        if (typeof onMount === 'function') {
            onMount(geocoder);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return null;
});

const noop = () => {};
